import React, { useState, useEffect, useRef, useContext } from "react";
import styled from "styled-components";
import Request from "common/Request";
import RangeSliderInput from "common/RangeSliderInput";
import NVLineChart from "common/graphs/NVLineChart";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import { parseRTTTable, requestRTT, mergeWith } from "./api";
import { labelRangesRetrieval } from "views/Status/TCPOComparison/component";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import { useScreenSize } from "common/hooks";
import { getSampleSize } from "common/api";
import { hoursScopeChoices, HOURS_TO_PERIOD } from "common/constants";

const SelectWrapper = styled.div`
  width: 150px;
`

const TileContainer = styled.div`
  min-height: 9.5cm;
  display: flex;
  flex-direction: column;
  & > h4 {
    text-align: center;
    margin: 20px 0 0 0;
  }
  & > .graph {
    flex: 1 1 100%;
    display: flex;
    .nv-y.nv-axis .nv-axisMaxMin {
      display: none;
    }
  }
  &.spread {
    justify-content: space-between;
    padding: 1em;
    align-content: flex-start;
  }
  &.can-compress {
    min-height: 0;
  }
  .input-group {
    max-width: 20ch;
    margin-bottom: 0;
  }
`;

export const ViewContainer = styled.div`
  min-height: 50vh;
  &.card {
    height: 100%;
  }
`;

const doesNothing = () => {};

const SingleColumnGrid = styled.div`
  display: grid;
  flex: 1 1 100%;
`;

const requestTrackedAndOptimized = (direction = "down", days, mode, bqnId) =>
  Promise.all([
    requestRTT({ type: "optimized", direction, days, mode, bqnId }).then(parseRTTTable),
    requestRTT({ type: "tracked", direction, days, mode, bqnId }).then(parseRTTTable),
  ])
    .then(([optimized, tracked]) => mergeWith("optimized", optimized, tracked))
    .catch(debug);

const requestAcceleration = (direction = "down", days, mode, bqnId) =>
  requestRTT({ type: "acceleration", direction, days, mode, bqnId })
    .then(parseRTTTable)
    .catch(debug);

const responseAsResult = (response) => ({ result: response });

export const FilteringSettings = ({
  direction = "down",
  hours = 24,
  onHoursChange = doesNothing,
  children,
  type = null,
}) => {
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const [request, setRequest] = useState(null);
  const actions = useContext(ActionsContext);
  const doLoad = (hours = 24) => {
    const days = hours / 24;
    setRequest(
      (type === null
        ? requestTrackedAndOptimized(direction, days, mode, bqnId)
        : requestAcceleration(direction, days, mode, bqnId)
      ).then(responseAsResult)
    );
  };
  useEffect(() =>
    doLoad(hours) || actions.recv("do-load", () => doLoad(hours))
  , [hours, bqnId]);

  const handleChange = ({ target }) => {
    const hours = parseInt(target.value);
    onHoursChange(hours);
  };

  return (
    <>
      <TileContainer className="can-compress">
        <SelectWrapper>
          <SelectInputWithIcon
            title="Date Range"
            name="Date Range"
            icon="date_range"
            selected={hours || 24}
            onChange={handleChange}
            options={hoursScopeChoices}
          />
        </SelectWrapper>
      </TileContainer>
      {request === null ? null : (
        <Request during={request}>
          {({ result }) => children({ result, hours })}
        </Request>
      )}
    </>
  );
};

export const HeaderDiv = styled.div`
  display: flex & .title {
    flex: 100% 1 1;
    padding-right: 2.5ch;
  }
`;

const skipTimeOffset = (input) => input.split(" ").splice(0, 5).join(" ");

const requestCurrentTime = () =>
  ifCl
    .run("show system date")
    .then(skipTimeOffset)
    .then((input) => new Date(input))
    .catch(
      (error) =>
        console.warn("failed to parse current time:", error) || new Date()
    );

const substractHours = (date, hours) => {
  date.setHours(date.getHours() - hours);
  return date;
};

const getTimeExtent = (hours) =>
  requestCurrentTime().then((current) => ({
    to: current,
    from: substractHours(new Date(current), hours),
  }));

const alonsideRange = (range, items) =>
  range === null || items.length === 0
    ? items
    : extentToRangeLimits(range, items.filter(isInsideRange(range)));

const extentToRangeLimits = (range, items) => [
  { time: range.from },
  ...items,
  { time: range.to },
];

const isInsideRange =
  ({ from, to }, field = "time") =>
  (item) =>
    from <= item[field] && item[field] <= to;

const formatUnit = (units) => (value) =>
  value === null || value === undefined
    ? "n/a"
    : `${value.toFixed(2)} ${units}`;

const FacetGraph = ({
  facet,
  title,
  range,
  units = "Mbps",
  fields,
  noDataMessage = "Not enought samples",
  items = [],
}) => {
  const wrapper = useRef(null);
  return (
    <TileContainer>
      <h4>{title}</h4>
      <div className="graph" ref={wrapper}>
        <NVLineChart
          facet={facet}
          items={alonsideRange(range, items)}
          xField="time"
          yAxisUnits={units}
          xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
          yTooltipFormat={formatUnit(units)}
          yAxisTopGap={0.05}
          yAxisBottomGap={0.05}
          yAxisBottomValue={0}
          fields={fields}
          noDataMessage={noDataMessage}
          skipVoid={true}
        />
      </div>
    </TileContainer>
  );
};

const GraphWrapper = styled.div`
  min-height: 9.5cm;
  display: flex;
  flex-direction: column;
`;

const Graph = ({ data = [], fields = [], title, range, units, ...rest }) => (
  <GraphWrapper>
    <FacetGraph
      items={data}
      fields={fields}
      title={title}
      range={range}
      units={units}
      {...rest}
    />
  </GraphWrapper>
);

const getInrangeItems = ({data, range, sampleSize}) => {
  const { from, to } = range;
  let inrangeItems = [];
  let maxItems;
  for (let i = 0; i < data.length; i += 1) {
    const { time } = data[i];
    if (from <= time && time <= to) {
      if (!maxItems) {
        maxItems = { ...data[i] };
      }
      if (i % sampleSize === 0) {
        inrangeItems.push(maxItems);
        maxItems = { ...data[i] };
      } else {
        const newMax = Object.entries(data[i]).reduce((acc, [key, value]) => {
          if(key !== 'time'){
            if(!acc[key]){
              acc[key] = value;
            } else {
              if (value > acc[key]){
                acc[key] = value;
              }
            }
          }
          return acc;
        }, maxItems);
        maxItems = newMax;
      }
    }
  }
  return inrangeItems;
};

const Graphs = ({ data = [], range = null, facets = [] }) => (
    <ColumnsGrid gap="1rem" columns={2} minWidth="9cm" rowGap="2rem">
      {range !== null
        ? facets.map(({ fields, label, units, ...rest }) => (
            <Graph
              key={label}
              data={data}
              title={label}
              range={range}
              fields={fields}
              units={units}
              {...rest}
            />
          ))
        : null}
    </ColumnsGrid>
);

const debug = (error) => {
  console.error(error);
  debugger;
};

export const PeriodContext = ({ hours, ...rest }) => {
  const [range, setRange] = useState(null);
  useEffect(() => {
    getTimeExtent(hours).then(setRange);
  }, [hours]);
  return range === null ? null : (
    <Report availableRange={range} hours={hours} {...rest} />
  );
};

const Report = ({ result = [], hours = 24, facets = [], availableRange }) => {
  const [range, setRange] = useState(availableRange && { ...availableRange });
  const { width: windowWidth } = useScreenSize();
  const POINTS_PER_TILE_CHART = windowWidth / 2;

  useEffect(() => {
    setRange(availableRange);
  }, [availableRange]);
  const period = HOURS_TO_PERIOD[hours];

  const sampleSize = getSampleSize(range, POINTS_PER_TILE_CHART, period);
  const inRangeItems = getInrangeItems({data: result, range, sampleSize});

  return (
    <SingleColumnGrid>
      <TileContainer className="full-width can-compress">
        {range === null ? null : (
          <Graphs data={inRangeItems} range={range} facets={facets} hours={hours} />
        )}
        {availableRange !== undefined ? (
          <RangeSliderInput {...availableRange} onChange={setRange} className="margin-t-30"/>
        ) : null}
      </TileContainer>
    </SingleColumnGrid>
  );
};

const removeGlobal = (source) =>
  Object.fromEntries(
    Object.entries(source).map(([field, value]) => [
      field.replace(/^Global-/, ""),
      value,
    ])
  );

export const LabelRetrieval = ({ children }) => {
  const [request, setRequest] = useState(null);
  const doLoad = () => setRequest(labelRangesRetrieval().then(removeGlobal));
  useEffect(() => {
    doLoad();
  }, []);
  return request === null ? null : (
    <Request during={request}>{(labelRanges) => children(labelRanges)}</Request>
  );
};
